import { useEffect, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Alert,
  ApiError,
  Badge,
  InfoTooltip,
  Spinner,
  fmtDate,
  type PartnerEnrollmentDetail,
  type MailingAddress,
  type PermanentAddress,
  type PharmacyAddress,
  type Prescription,
} from "@dearbill/shared";
import { useApi } from "../auth/useApi";
import { usePartnerMe } from "../auth/usePartnerMe";
import { useOrg } from "../shell/OrgContext";
import { CaseStatusLabel } from "../components/referrals/CaseStatusLabel";
import iconPrinter from "../assets/icon-printer.svg";
import iconSignature from "../assets/icon-signature.svg";

type AnyAddress = PermanentAddress | MailingAddress | PharmacyAddress;

/** Join the non-empty parts of an address into a single readable line. */
function formatAddress(addr: AnyAddress | null | undefined): string {
  if (!addr) return "";
  const street2 = "street2" in addr ? addr.street2 : "";
  const line1 = [addr.street1, street2].filter(Boolean).join(", ");
  const cityState = [addr.city, addr.state].filter(Boolean).join(", ");
  return [line1, [cityState, addr.zip].filter(Boolean).join(" ")].filter(Boolean).join(" · ");
}

/** One drug line: "drugName (form)", dropping the parens when `form` is blank. */
function formatPrescription(p: Prescription): string {
  return p.form.trim() ? `${p.drugName} (${p.form})` : p.drugName;
}

/** Month and year ("Born Apr 1950") from the date-only `dob`, read in UTC. */
function fmtBorn(dob: string): string {
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return "";
  return `Born ${d.toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" })}`;
}

/**
 * Read-only detail view for one referral (`/orgs/:orgId/forms/:enrollmentId`).
 *
 * Shows the patient header with a Print page button, a signature banner while
 * the signature is outstanding, and the case status read off the wire
 * (`caseStatus`, `caseStatusDetail`, derived server-side). A what's-happened
 * list uses only real timestamps (`createdAt`, `signedAt`). A summary column
 * shows the assigned plan, the program status, the help line and a privacy
 * note. Below that comes every submitted form field the backend exposes,
 * display-only. Sections that don't apply (no separate mailing address) are
 * hidden.
 *
 * A 404 or 403 renders a "not available" notice. The server answers 404 for an
 * unknown id, another org's referral, a colleague's referral a regular `user`
 * can't see, and an unshared broker referral. A 401 is handled in `useApi`,
 * which clears the session.
 */
export function EnrollmentDetail(): ReactNode {
  const api = useApi();
  const { orgId } = useOrg();
  const { me } = usePartnerMe();
  const { enrollmentId = "" } = useParams<{ enrollmentId: string }>();

  const [detail, setDetail] = useState<PartnerEnrollmentDetail | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setNotFound(false);
    setLoadError(null);
    api<PartnerEnrollmentDetail>(`/api/partner/orgs/${orgId}/enrollments/${enrollmentId}`)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) return;
        if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
          setNotFound(true);
          return;
        }
        setLoadError("We couldn't load this referral. Please try again.");
      });
    return () => {
      cancelled = true;
    };
  }, [api, orgId, enrollmentId]);

  // OrgShell redirects to /login without a session, so this route renders only
  // with a live session and needs no auth guard of its own.

  const backLink = (
    <Link
      to={`/orgs/${orgId}/forms`}
      className="text-ember hover:text-ember-dark inline-flex items-center py-2 text-[17px] font-semibold underline underline-offset-2"
    >
      &larr; All referrals
    </Link>
  );

  if (notFound) {
    return (
      <div>
        {backLink}
        <p className="border-border-strong text-slate-teal mt-4 rounded-2xl border bg-white px-6 py-8 text-center">
          This referral isn&apos;t available. Use &ldquo;All referrals&rdquo; above to see the
          referrals you can view.
        </p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div>
        {backLink}
        <Alert tone="error" title={loadError} className="mt-4">
          Reload the page to try again. If it keeps happening, use &ldquo;Need help? Call&rdquo; at
          the top of the page.
        </Alert>
      </div>
    );
  }

  if (detail === null) {
    return (
      <div>
        {backLink}
        <div className="text-slate-teal mt-4 flex items-center gap-3">
          <Spinner /> <span>Loading referral…</span>
        </div>
      </div>
    );
  }

  const byLabel = me && detail.partnerUserId === me.userId ? "you" : detail.partnerUserName || "—";
  const metaLine = [
    fmtBorn(detail.dob),
    detail.phone,
    `Referred by ${byLabel} on ${fmtDate(detail.createdAt)}`,
  ]
    .filter(Boolean)
    .join(" · ");

  // The what's-happened list uses only real timestamps. The backend exposes no
  // e-sign-sent or status-change timestamps, so the current state renders as
  // the case-status detail line above the dated events.
  const events: { date: string; title: string; body: string }[] = [];
  if (detail.signedAt !== null) {
    events.push({
      date: fmtDate(detail.signedAt),
      title: "They signed their form",
      body: "DearBill received the signed enrollment form.",
    });
  }
  events.push({
    date: fmtDate(detail.createdAt),
    title: byLabel === "you" ? "You referred them" : `${byLabel} referred them`,
    body: "The referral form was submitted to DearBill.",
  });

  return (
    <div>
      {backLink}

      <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-foreground font-serif text-[30px] font-semibold leading-[42px]">
            {detail.fullName || "Referral"}
          </h2>
          <p className="text-slate-teal mt-1 text-base leading-[22px]">{metaLine}</p>
        </div>
        <button
          type="button"
          onClick={() => window.print()}
          className="bg-soft text-midnight hover:bg-soft-hover inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg px-5 py-3 text-[15px] font-semibold leading-[1.5] transition print:hidden"
        >
          <img src={iconPrinter} alt="" aria-hidden="true" className="size-[18px]" />
          Print page
        </button>
      </div>

      {detail.caseStatus === "Needs attention" && (
        <div className="border-ember mt-5 rounded-xl border bg-white px-6 py-5 shadow-[0px_4px_12px_0px_rgba(0,0,0,0.07)]">
          <p className="text-midnight flex items-center gap-2 font-serif text-[22px] font-semibold leading-7">
            <img src={iconSignature} alt="" aria-hidden="true" className="size-6" />
            Signature needed
          </p>
          <p className="text-midnight mt-1.5 text-base leading-6">{detail.caseStatusDetail}</p>
        </div>
      )}

      <div className="mt-5 grid items-start gap-6 lg:grid-cols-[1fr_360px]">
        <section className="border-border-strong overflow-hidden rounded-2xl border bg-white">
          <div className="bg-ice px-6 py-4">
            <h3 className="text-slate-teal text-[11px] font-semibold uppercase leading-[18px] tracking-[1.1px]">
              Patient Status
            </h3>
          </div>
          <div className="flex flex-wrap items-start gap-x-4 gap-y-1 px-6 py-4">
            <CaseStatusLabel status={detail.caseStatus} />
            <p className="text-foreground min-w-0 flex-1 basis-56 text-[17px] leading-6">
              {detail.caseStatusDetail}
            </p>
          </div>
          {events.map((ev) => (
            <div key={ev.title} className="border-haze flex gap-4 border-t px-6 py-4">
              <p className="text-slate-teal w-28 shrink-0 text-[15px] leading-[22px]">{ev.date}</p>
              <div className="min-w-0">
                <p className="text-foreground text-[17px] font-semibold leading-6">{ev.title}</p>
                <p className="text-foreground mt-0.5 text-base leading-6">{ev.body}</p>
              </div>
            </div>
          ))}
        </section>

        <div className="flex flex-col gap-4">
          <section className="border-border-strong flex flex-col gap-4 rounded-2xl border bg-white p-[22px]">
            <div>
              <p className="text-slate-teal text-[13px] font-semibold uppercase leading-[18px] tracking-[0.8px]">
                Patient&rsquo;s plan
              </p>
              <p className="text-foreground mt-1 flex flex-wrap items-center gap-2 text-[17px] font-semibold leading-6">
                <span>
                  {detail.plan
                    ? [detail.plan.planName, detail.plan.insurerName].filter(Boolean).join(" · ")
                    : "Not assigned yet"}
                </span>
                {/* A plan picked for a later contract year than the clock's
                    is not in force yet; a null year reads as this year. */}
                {detail.planYear !== null && detail.planYear > new Date().getFullYear() && (
                  <Badge tone="neutral">Starts {detail.planYear}</Badge>
                )}
              </p>
            </div>
            <div>
              <p className="text-slate-teal text-[13px] font-semibold uppercase leading-[18px] tracking-[0.8px]">
                Affordability program status
              </p>
              {/* `programStatus` is its own wire field, never merged into the
                  case-status label. Null means no program has been assessed. */}
              <p className="text-foreground mt-1 text-base leading-[22px]">
                {detail.programStatus
                  ? `${detail.programStatus.program} · ${detail.programStatus.status}`
                  : "Not assessed yet"}
              </p>
            </div>
          </section>

          <section className="border-border-strong flex flex-col gap-2 rounded-2xl border bg-white p-[22px]">
            <p className="text-slate-teal text-[13px] font-semibold uppercase leading-[18px] tracking-[0.8px]">
              Questions about this referral
            </p>
            <p className="text-foreground text-[17px] font-semibold leading-6">DearBill support</p>
            <a
              href="tel:+18552647130"
              className="text-foreground text-base font-medium hover:underline"
            >
              855-264-7130
            </a>
          </section>

          <section className="bg-frost rounded-2xl p-[18px]">
            <p className="text-deep-navy text-[15px] leading-[22px]">
              You can see this patient&rsquo;s progress because your organization referred them.
              Their income, other medications, and anything they discuss with DearBill directly stay
              between them and DearBill.
            </p>
          </section>
        </div>
      </div>

      <div className="mt-6 max-w-3xl space-y-5">
        <Section title="Patient">
          <Field label="Full name" value={detail.fullName} />
          <Field label="Suffix" value={detail.suffix} />
          <Field label="Pronouns" value={detail.pronouns} />
          <Field label="Date of birth" value={detail.dob ? fmtDate(detail.dob) : ""} />
          <Field label="Gender" value={detail.gender} />
        </Section>

        <Section title="Addresses">
          <Field label="Permanent address" value={formatAddress(detail.permanent)} />
          {detail.hasSeparateMailingAddress && (
            <Field label="Mailing address" value={formatAddress(detail.mailing)} />
          )}
        </Section>

        <Section title="Contact">
          <Field label="Phone" value={detail.phone} />
          <Field label="Home phone" value={detail.homePhone} />
          <Field label="Email" value={detail.email} />
        </Section>

        {/* The assigned M3P plan reads as dashes until an admin assigns one. */}
        <Section title="Plan">
          <Field label="Plan" value={detail.plan?.planName ?? null} />
          <Field label="Insurer" value={detail.plan?.insurerName ?? null} />
          <Field
            label={
              <>
                Medicare ID (MBI){" "}
                <InfoTooltip
                  label="About the Medicare ID"
                  text="The patient's Medicare Beneficiary Identifier — the 11-character number on their red-white-and-blue Medicare card."
                />
              </>
            }
            value={detail.mbi}
          />
        </Section>

        <Section title="Pharmacy">
          <Field label="Name" value={detail.pharmacy.name} />
          <Field label="Phone" value={detail.pharmacy.phone} />
          <Field label="Address" value={formatAddress(detail.pharmacy.address)} />
          <Field
            label="Prescriptions"
            value={
              detail.prescriptionsList.length > 0 ? (
                <ul className="space-y-0.5">
                  {detail.prescriptionsList.map((p) => (
                    <li key={p.rxcui}>{formatPrescription(p)}</li>
                  ))}
                </ul>
              ) : (
                ""
              )
            }
          />
        </Section>

        <p className="text-slate-teal text-sm">
          Submitted by{" "}
          <span className="text-foreground font-medium">{detail.partnerUserName || "—"}</span>
        </p>
      </div>
    </div>
  );
}

/** A titled card grouping a set of read-only fields. */
function Section({ title, children }: { title: ReactNode; children: ReactNode }): ReactNode {
  return (
    <section className="border-border-strong rounded-2xl border bg-white px-6 py-5">
      <h3 className="text-slate-teal text-[13px] font-semibold uppercase leading-[18px] tracking-[0.8px]">
        {title}
      </h3>
      <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">{children}</dl>
    </section>
  );
}

/** One read-only label/value pair. Empty values render a dash. */
function Field({ label, value }: { label: ReactNode; value: ReactNode }): ReactNode {
  const isEmpty =
    value === null || value === undefined || (typeof value === "string" && value.trim() === "");
  return (
    <div>
      <dt className="text-slate-teal text-sm font-medium">{label}</dt>
      <dd className="text-foreground mt-0.5">
        {isEmpty ? <span className="text-slate-teal">—</span> : value}
      </dd>
    </div>
  );
}
